import loggingApi from '@sleepstation/logger'
import {
  curry,
  equals,
  find,
  findIndex,
  head,
  init,
  last,
  lens,
  pipe,
  prop,
  propEq,
  tail,
  test,
  update,
  when,
} from 'ramda'
import { isFunction, isRegExp, isUndefined } from 'ramda-adjunct'
import urlParse from 'url-parse'
import LOG_GROUP from '../const/logGroup'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const logger = loggingApi.getLogger({
  group: LOG_GROUP.ROUTER,
})

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

export const propEqRoute = (route) => propEq(route, `route`)

const findByRoute = curry((route, a) => find(propEqRoute(route), a))

export const updateByRoute = curry((route, value, a) => {
  const index = findIndex(propEqRoute(route), a)
  return index > -1 ? update(index, value, a) : a
})

const getLastCharIsSlash = pipe(last, equals(`/`))
const getFirstCharIsSlash = pipe(head, equals(`/`))

const stripTrailingSlash = when(getLastCharIsSlash, init)
const stripLeadingSlash = when(getFirstCharIsSlash, tail)
const stripLeadingAndTrailingSlash = pipe(stripLeadingSlash, stripTrailingSlash)

const getRoute = pipe(urlParse, prop(`pathname`), tail, stripTrailingSlash)

// -----------------------------------------------------------------------------
// Exports
// -----------------------------------------------------------------------------

export const lensByRoute = (route) =>
  lens(
    // Getter
    findByRoute(route),
    // Setter
    updateByRoute(route)
  )

export const findTemplateByRoute = (currentRoute, content) =>
  find(({ route }) => {
    if (isUndefined(route)) {
      return false
    }
    const currentRouteStripped = stripLeadingAndTrailingSlash(currentRoute)

    if (isRegExp(route)) {
      return test(route, currentRouteStripped)
    }

    const routeStripped = stripLeadingAndTrailingSlash(route)
    return equals(currentRouteStripped, routeStripped)
  }, content.template)

export const findTemplateByName = (name, content) =>
  find(propEq(name, `name`), content.template)

export const getPageDataFromTemplateByUrl = ({ request, params }, content) => {
  const { url } = request

  const route = getRoute(url)
  const template = findTemplateByRoute(route, content)

  if (!template) {
    logger.error(`No template for route: '${route}'`)
  }

  const { getData, data } = template

  return isFunction(getData) ? getData({ request, params }) : data
}

export const getPageData = (name, params, content) => {
  const template = findTemplateByName(name, content)
  const page = template.getPageData(params)
  return {
    page,
  }
}

export const getPageDataForRequest = (props, content) => {
  const { request, params } = props
  const { url } = request

  try {
    const data = getPageDataFromTemplateByUrl({ request, params }, content)
    logger.info(`Got page data for route '${url}'`, data)
    return data
  } catch (error) {
    logger.error(`There was a problem getting page data for route: '${url}'`, {
      originalError: error,
    })
    throw error
  }
}
